import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, matchPath, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import { IconMenu, IconMessages, IconSidebar, IconSignOut } from './NavIcons';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout } from '../features/auth/authSlice';
import { HOME_PATH, NAV_SECTIONS } from '../app/navigation';

export default function WorkspaceShell() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const CompanyIcon = NAV_SECTIONS[0].items[0].icon;
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('micus_consult_menu_collapsed') === '1');
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => localStorage.setItem('micus_consult_menu_collapsed', collapsed ? '1' : '0'), [collapsed]);

  const current = useMemo(() => {
    if (matchPath('/companies/:id', location.pathname)) return 'Company workspace';
    return 'Companies';
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-row-reverse bg-[#faf7f9] text-[#1a0010]">
      <aside className={`sticky top-0 hidden h-screen shrink-0 flex-col bg-[#3d001f] px-3 py-4 text-white transition-[width] lg:flex ${collapsed ? 'w-[72px]' : 'w-[224px]'}`} aria-label="Consult menu">
        <div className={`mb-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between px-2'}`}>
          {!collapsed && <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Menu</span>}
          <button type="button" onClick={() => setCollapsed((value) => !value)} className="rounded-lg p-1.5 text-white/75 hover:bg-white/10 hover:text-white" aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} title={collapsed ? 'Expand menu' : 'Collapse menu'}>
            <IconSidebar className={`h-5 w-5 ${collapsed ? 'scale-x-[-1]' : ''}`} />
          </button>
        </div>
        <nav className="flex flex-col gap-1" aria-label="Consult workspace">
          <NavLink to="/companies" title={collapsed ? 'Companies' : undefined} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold ${collapsed ? 'justify-center' : ''} ${isActive || location.pathname.startsWith('/companies/') ? 'bg-white text-[#d6006e]' : 'text-white/85 hover:bg-white/10'}`}>
            <CompanyIcon className="h-5 w-5 shrink-0" />
            {!collapsed && 'Companies'}
          </NavLink>
          <NavLink to="/messages" title={collapsed ? 'Messages' : undefined} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold ${collapsed ? 'justify-center' : ''} ${isActive ? 'bg-white text-[#d6006e]' : 'text-white/85 hover:bg-white/10'}`}>
            <IconMessages className="h-5 w-5 shrink-0" />
            {!collapsed && 'Messages'}
          </NavLink>
        </nav>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="mz-slide-in-right absolute inset-y-0 right-0 w-[280px] bg-[#3d001f] p-4 shadow-2xl">
            <button type="button" aria-label="Close menu" onClick={() => setMobileOpen(false)} className="mb-4 rounded-lg px-3 py-2 text-sm font-bold text-white hover:bg-white/10">Close</button>
            <NavLink to="/companies" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-xl bg-white px-3 py-3 text-sm font-bold text-[#d6006e]">
              <CompanyIcon className="h-5 w-5" /> Companies
            </NavLink>
            <NavLink to="/messages" onClick={() => setMobileOpen(false)} className="mt-1 flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-bold text-white/85 hover:bg-white/10">
              <IconMessages className="h-5 w-5" /> Messages
            </NavLink>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[#fce7f3] bg-white/90 px-5 py-3 backdrop-blur-md sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link to={HOME_PATH} className="flex shrink-0 items-center gap-2.5">
              <BrandLogo size={28} />
              <span className="hidden text-sm font-black text-[#6b0030] sm:inline">Micus Consult</span>
            </Link>
            <div className="hidden min-w-0 border-l border-[#fce7f3] pl-3 sm:block">
              <p className="truncate text-[10px] font-bold uppercase tracking-[0.2em] text-[#c084a0]">Companies</p>
              <p className="truncate text-sm font-black text-[#6b0030]">{current}</p>
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <div className="hidden min-w-0 text-right sm:block">
              <p className="max-w-[220px] truncate text-xs font-bold text-[#6b0030]">{user?.name || user?.email}</p>
              <p className="max-w-[220px] truncate text-[10px] text-[#b0708f]">{user?.email}</p>
            </div>
            <button type="button" onClick={() => dispatch(logout())} title="Sign out" aria-label="Sign out" className="rounded-lg p-2 text-[#d6006e] hover:bg-[#fdf2f8]">
              <IconSignOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-[#d6006e] lg:hidden"
              style={{ background: '#fdf2f8', border: '1px solid #fce7f3' }}
            >
              <IconMenu className="h-4 w-4" /> Menu
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div className="mx-auto w-full max-w-[1680px] px-4 py-5 sm:px-6 sm:py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
